import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Edit, Trash2, X, Building2, CheckCircle, AlertTriangle, Eye, Download, Upload, FileSpreadsheet 
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchTenantBranding } from '../utils/tenantBranding';
import { useSearchParams } from 'react-router-dom';
import { ClickableCard, ActiveFilterChip } from '../components/ClickableCard';

const SUPPLIER_CARDS = [
  { key: 'all', label: 'Total Suppliers', tone: '' },
  { key: 'active', label: 'Active', tone: 'text-green-600', test: (s) => s.status === 'Active' },
  { key: 'critical', label: 'Critical', tone: 'text-red-600', test: (s) => s.criticality === 'Critical' },
  { key: 'high_risk', label: 'High Risk', tone: 'text-slate-600', test: (s) => s.risk_level === 'High' || s.risk_level === 'Critical' },
  { key: 'compliant', label: 'Compliant', tone: 'text-green-600', test: (s) => s.compliance_status === 'Compliant' },
];

export default function SupplierRegisterPage() {
  const { viewingOrg } = useContext(AuthContext);
  const vtq = () => (viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '');
  const vtp = () => (viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '');

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [viewingSupplier, setViewingSupplier] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCriticality, setFilterCriticality] = useState('all');
  const [searchParams] = useSearchParams();
  const [cardKey, setCardKey] = useState(() => searchParams.get('card') || 'all');
  const activeCard = SUPPLIER_CARDS.find((c) => c.key === cardKey);

  const [formData, setFormData] = useState({
    supplier_id: '',
    name: '',
    description: '',
    category: '',
    service_provided: '',
    criticality: 'Medium',
    risk_level: 'Medium',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    contract_start_date: '',
    contract_end_date: '',
    contract_value: '',
    compliance_status: 'Not Assessed',
    certifications: '',
    related_controls: '',
    related_policies: '',
    data_processed: '',
    access_level: '',
    sla_terms: '',
    notes: '',
    next_review_date: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchSuppliers();
  }, [refreshKey, viewingOrg]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/suppliers${vtq()}`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        certifications: formData.certifications ? formData.certifications.split(',').map(c => c.trim()) : [],
        related_controls: formData.related_controls ? formData.related_controls.split(',').map(c => c.trim()) : [],
        related_policies: formData.related_policies ? formData.related_policies.split(',').map(p => p.trim()) : []
      };

      if (editingSupplier) {
        await axios.patch(`${API}/suppliers/${editingSupplier.id}${vtq()}`, payload);
        toast.success('Supplier updated successfully');
      } else {
        await axios.post(`${API}/suppliers${vtq()}`, payload);
        toast.success('Supplier created successfully');
      }

      setShowModal(false);
      resetForm();
      refreshData();
    } catch (error) {
      toast.error(editingSupplier ? 'Failed to update supplier' : 'Failed to create supplier');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_id: supplier.supplier_id,
      name: supplier.name,
      description: supplier.description || '',
      category: supplier.category,
      service_provided: supplier.service_provided,
      criticality: supplier.criticality,
      risk_level: supplier.risk_level,
      contact_name: supplier.contact_name || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      address: supplier.address || '',
      website: supplier.website || '',
      contract_start_date: supplier.contract_start_date || '',
      contract_end_date: supplier.contract_end_date || '',
      contract_value: supplier.contract_value || '',
      compliance_status: supplier.compliance_status,
      certifications: supplier.certifications ? supplier.certifications.join(', ') : '',
      related_controls: supplier.related_controls ? supplier.related_controls.join(', ') : '',
      related_policies: supplier.related_policies ? supplier.related_policies.join(', ') : '',
      data_processed: supplier.data_processed || '',
      access_level: supplier.access_level || '',
      sla_terms: supplier.sla_terms || '',
      notes: supplier.notes || '',
      next_review_date: supplier.next_review_date || '',
      status: supplier.status
    });
    setShowModal(true);
  };

  const handleDelete = async (supplierId, supplierName) => {
    if (!window.confirm(`Are you sure you want to delete "${supplierName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/suppliers/${supplierId}${vtq()}`);
      toast.success('Supplier deleted successfully');
      refreshData();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const handleAssess = async (supplierId) => {
    try {
      await axios.post(`${API}/suppliers/${supplierId}/assess${vtq()}`);
      toast.success('Supplier assessment recorded');
      refreshData();
    } catch (error) {
      toast.error('Failed to record assessment');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      name: '',
      description: '',
      category: '',
      service_provided: '',
      criticality: 'Medium',
      risk_level: 'Medium',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      website: '',
      contract_start_date: '',
      contract_end_date: '',
      contract_value: '',
      compliance_status: 'Not Assessed',
      certifications: '',
      related_controls: '',
      related_policies: '',
      data_processed: '',
      access_level: '',
      sla_terms: '',
      notes: '',
      next_review_date: '',
      status: 'Active'
    });
    setEditingSupplier(null);
  };

  const getCriticalityBadge = (criticality) => {
    const badges = {
      'Low': 'bg-slate-100 text-green-800',
      'Medium': 'bg-slate-100 text-slate-800',
      'High': 'bg-slate-100 text-slate-800',
      'Critical': 'bg-slate-100 text-red-800'
    };
    return badges[criticality] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Active': 'bg-slate-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-600',
      'Pending': 'bg-slate-100 text-slate-800',
      'Terminated': 'bg-slate-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getComplianceBadge = (compliance) => {
    const badges = {
      'Compliant': 'bg-slate-100 text-green-800',
      'Non-Compliant': 'bg-slate-100 text-red-800',
      'Under Review': 'bg-slate-100 text-slate-800',
      'Not Assessed': 'bg-gray-100 text-gray-600'
    };
    return badges[compliance] || 'bg-gray-100 text-gray-800';
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const statusMatch = filterStatus === 'all' || supplier.status === filterStatus;
    const criticalityMatch = (filterCriticality === 'all' || supplier.criticality === filterCriticality)
      && (!activeCard?.test || activeCard.test(supplier));
    return statusMatch && criticalityMatch;
  });

  const exportToCSV = async () => {
    if (suppliers.length === 0) {
      toast.error('No suppliers to export');
      return;
    }
    try {
      const branding = await fetchTenantBranding(viewingOrg?.id);
      const brandTitle = branding?.name ? `${branding.name} — Supplier Register` : 'Supplier Register';
      const stamp = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
      const headers = ['Supplier ID', 'Name', 'Category', 'Service Provided', 'Criticality', 'Risk Level', 'Compliance Status', 'Status', 'Contact Name', 'Contact Email', 'Contact Phone', 'Website', 'Contract Start', 'Contract End', 'Contract Value', 'Certifications', 'Data Processed', 'Access Level', 'SLA Terms', 'Next Review Date', 'Notes'];
      const rows = suppliers.map(s => [
        s.supplier_id,
        s.name,
        s.category,
        s.service_provided,
        s.criticality,
        s.risk_level,
        s.compliance_status,
        s.status,
        s.contact_name || '',
        s.contact_email || '',
        s.contact_phone || '',
        s.website || '',
        s.contract_start_date || '',
        s.contract_end_date || '',
        s.contract_value || '',
        Array.isArray(s.certifications) ? s.certifications.join('; ') : (s.certifications || ''),
        s.data_processed || '',
        s.access_level || '',
        s.sla_terms || '',
        s.next_review_date || '',
        s.notes || ''
      ]);
      const csvContent = [
        `"${brandTitle.replace(/"/g, '""')}"`,
        `"Generated at ${stamp}"`,
        '',
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `Supplier_Register_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Supplier register exported successfully');
    } catch (e) {
      toast.error('Failed to export suppliers');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading suppliers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-register-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Supplier Register
          </h1>
          <p className="text-slate-600 mt-2">Manage third-party suppliers and vendors</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/suppliers-template`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'supplier_register_template.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Template downloaded');
              } catch {
                toast.error('Failed to download template');
              }
            }}
            variant="outline"
            className="rounded-sm"
            data-testid="download-supplier-template-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            onClick={() => { setImportFile(null); setImportResult(null); setShowImportDialog(true); }}
            variant="outline"
            className="rounded-sm"
            data-testid="import-suppliers-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="rounded-sm"
            data-testid="export-suppliers-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Supplier
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-sm border border-input bg-background px-4 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
          <option value="Terminated">Terminated</option>
        </select>

        <select
          value={filterCriticality}
          onChange={(e) => setFilterCriticality(e.target.value)}
          className="rounded-sm border border-input bg-background px-4 py-2 text-sm"
        >
          <option value="all">All Criticality</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Critical">Critical</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {SUPPLIER_CARDS.map((c) => (
          <ClickableCard
            key={c.key}
            active={cardKey === c.key}
            onClick={() => setCardKey(cardKey === c.key ? 'all' : c.key)}
            testid={`supplier-card-${c.key}`}
            className="rounded-sm"
          >
            <div className="bg-white border border-border rounded-sm p-4 h-full">
              <p className="text-sm text-slate-600">{c.label}</p>
              <p className={`text-2xl font-heading font-semibold mt-1 ${c.tone}`} data-testid={`supplier-stat-${c.key}`}>
                {c.test ? suppliers.filter(c.test).length : suppliers.length}
              </p>
            </div>
          </ClickableCard>
        ))}
      </div>

      {activeCard?.test && (
        <ActiveFilterChip label={`${activeCard.label} (${filteredSuppliers.length})`} onClear={() => setCardKey('all')} testid="supplier-active-filter" />
      )}

      {/* Suppliers Table */}
      <div className="border border-border rounded-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Supplier ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Criticality</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Compliance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono text-slate-900">{supplier.supplier_id}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{supplier.name}</div>
                        <div className="text-xs text-slate-700">{supplier.service_provided}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-slate-700">{supplier.category}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCriticalityBadge(supplier.criticality)}`}>
                      {supplier.criticality}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getComplianceBadge(supplier.compliance_status)}`}>
                      {supplier.compliance_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(supplier.status)}`}>
                      {supplier.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewingSupplier(supplier)}
                        className="text-slate-600 hover:text-slate-800"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(supplier)}
                        className="text-slate-600 hover:text-slate-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAssess(supplier.id)}
                        className="text-green-600 hover:text-green-800"
                        title="Record Assessment"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(supplier.id, supplier.name)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredSuppliers.length === 0 && (
        <div className="text-center py-12 border border-border rounded-sm bg-white">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No suppliers found</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">
                {editingSupplier ? 'Edit Supplier' : 'New Supplier'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="supplier_id">Supplier ID *</Label>
                    <Input
                      id="supplier_id"
                      value={formData.supplier_id}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                      placeholder="SUP-001"
                      required
                      disabled={editingSupplier}
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Supplier Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Acme Cloud Services"
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the supplier"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select category</option>
                      <option value="Cloud Provider">Cloud Provider</option>
                      <option value="SaaS">SaaS</option>
                      <option value="Software Vendor">Software Vendor</option>
                      <option value="Consulting">Consulting</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="Security Services">Security Services</option>
                      <option value="Support Services">Support Services</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="service_provided">Service Provided *</Label>
                    <Input
                      id="service_provided"
                      value={formData.service_provided}
                      onChange={(e) => setFormData({ ...formData, service_provided: e.target.value })}
                      placeholder="Cloud hosting, Email service, etc."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Risk & Compliance */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Risk & Compliance</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="criticality">Criticality *</Label>
                    <select
                      id="criticality"
                      value={formData.criticality}
                      onChange={(e) => setFormData({ ...formData, criticality: e.target.value })}
                      className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                      required
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="risk_level">Risk Level *</Label>
                    <select
                      id="risk_level"
                      value={formData.risk_level}
                      onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                      className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                      required
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="compliance_status">Compliance Status *</Label>
                    <select
                      id="compliance_status"
                      value={formData.compliance_status}
                      onChange={(e) => setFormData({ ...formData, compliance_status: e.target.value })}
                      className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                      required
                    >
                      <option value="Not Assessed">Not Assessed</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Compliant">Compliant</option>
                      <option value="Non-Compliant">Non-Compliant</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="certifications">Certifications</Label>
                    <Input
                      id="certifications"
                      value={formData.certifications}
                      onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                      placeholder="ISO 27001, SOC 2 (comma-separated)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_processed">Data Processed</Label>
                    <Input
                      id="data_processed"
                      value={formData.data_processed}
                      onChange={(e) => setFormData({ ...formData, data_processed: e.target.value })}
                      placeholder="Customer data, PII, etc."
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="access_level">Access Level</Label>
                  <select
                    id="access_level"
                    value={formData.access_level}
                    onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                  >
                    <option value="">Not specified</option>
                    <option value="None">None</option>
                    <option value="Read-Only">Read-Only</option>
                    <option value="Read-Write">Read-Write</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Contact Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main St, City, Country"
                    />
                  </div>
                </div>
              </div>

              {/* Contract Details */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Contract Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="contract_start_date">Contract Start</Label>
                    <Input
                      id="contract_start_date"
                      type="date"
                      value={formData.contract_start_date}
                      onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contract_end_date">Contract End</Label>
                    <Input
                      id="contract_end_date"
                      type="date"
                      value={formData.contract_end_date}
                      onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contract_value">Contract Value</Label>
                    <Input
                      id="contract_value"
                      value={formData.contract_value}
                      onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                      placeholder="$10,000/year"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="sla_terms">SLA Terms</Label>
                  <Textarea
                    id="sla_terms"
                    value={formData.sla_terms}
                    onChange={(e) => setFormData({ ...formData, sla_terms: e.target.value })}
                    placeholder="99.9% uptime, 24/7 support, etc."
                    rows={2}
                  />
                </div>
              </div>

              {/* Additional */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Additional Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="related_controls">Related Controls</Label>
                    <Input
                      id="related_controls"
                      value={formData.related_controls}
                      onChange={(e) => setFormData({ ...formData, related_controls: e.target.value })}
                      placeholder="A.15.1.1, A.15.1.2 (comma-separated)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="related_policies">Related Policies</Label>
                    <Input
                      id="related_policies"
                      value={formData.related_policies}
                      onChange={(e) => setFormData({ ...formData, related_policies: e.target.value })}
                      placeholder="POL-001, POL-002 (comma-separated)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="next_review_date">Next Review Date</Label>
                    <Input
                      id="next_review_date"
                      type="date"
                      value={formData.next_review_date}
                      onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                      required
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Pending">Pending</option>
                      <option value="Terminated">Terminated</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes or comments"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  {editingSupplier ? 'Update Supplier' : 'Create Supplier'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingSupplier && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold">{viewingSupplier.name}</h2>
                <p className="text-sm text-slate-600 mt-1">{viewingSupplier.supplier_id}</p>
              </div>
              <button onClick={() => setViewingSupplier(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Service Provided</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingSupplier.service_provided}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Category</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingSupplier.category}</p>
                </div>
              </div>

              {viewingSupplier.description && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Description</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingSupplier.description}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Criticality</Label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getCriticalityBadge(viewingSupplier.criticality)}`}>
                    {viewingSupplier.criticality}
                  </span>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Risk Level</Label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getCriticalityBadge(viewingSupplier.risk_level)}`}>
                    {viewingSupplier.risk_level}
                  </span>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Compliance</Label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getComplianceBadge(viewingSupplier.compliance_status)}`}>
                    {viewingSupplier.compliance_status}
                  </span>
                </div>
              </div>

              {viewingSupplier.certifications && viewingSupplier.certifications.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Certifications</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {viewingSupplier.certifications.map((cert, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-100 text-slate-800 text-xs rounded-sm">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(viewingSupplier.contact_name || viewingSupplier.contact_email || viewingSupplier.contact_phone) && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Contact Information</Label>
                  <div className="mt-2 text-sm text-slate-900 space-y-1">
                    {viewingSupplier.contact_name && <p>Name: {viewingSupplier.contact_name}</p>}
                    {viewingSupplier.contact_email && <p>Email: {viewingSupplier.contact_email}</p>}
                    {viewingSupplier.contact_phone && <p>Phone: {viewingSupplier.contact_phone}</p>}
                  </div>
                </div>
              )}

              {viewingSupplier.contract_start_date && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Contract Period</Label>
                    <p className="mt-1 text-sm text-slate-900">
                      {new Date(viewingSupplier.contract_start_date).toLocaleDateString()} - 
                      {viewingSupplier.contract_end_date ? new Date(viewingSupplier.contract_end_date).toLocaleDateString() : 'Ongoing'}
                    </p>
                  </div>
                  {viewingSupplier.contract_value && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Contract Value</Label>
                      <p className="mt-1 text-sm text-slate-900">{viewingSupplier.contract_value}</p>
                    </div>
                  )}
                </div>
              )}

              {viewingSupplier.last_assessment_date && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Last Assessment</Label>
                  <p className="mt-1 text-sm text-slate-900">
                    {new Date(viewingSupplier.last_assessment_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Suppliers Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="import-suppliers-dialog">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Import Suppliers from Excel</h3>
              <button onClick={() => setShowImportDialog(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-medium text-slate-900 mb-1">How it works</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Download the template (above) for the expected columns.</li>
                  <li>Required: <b>Name, Category, Service Provided, Criticality, Risk Level</b></li>
                  <li>Criticality / Risk Level / Compliance / Status accept dropdown values.</li>
                  <li>Other templates work too as long as column names match.</li>
                </ul>
              </div>
              <div>
                <Label htmlFor="sup-file">Excel file (.xlsx)</Label>
                <Input
                  id="sup-file"
                  type="file"
                  accept=".xlsx,.xlsm"
                  data-testid="supplier-import-file-input"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="mt-1"
                />
                {importFile && <p className="text-xs text-slate-600 mt-1">Selected: {importFile.name}</p>}
              </div>
              {importResult && (
                <div className="rounded-sm border border-green-200 bg-green-50 p-3 text-sm">
                  <p className="font-medium text-green-900">
                    Imported {importResult.imported} supplier(s){importResult.skipped ? `, skipped ${importResult.skipped}` : ''}
                  </p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <ul className="mt-2 text-xs text-amber-900 list-disc list-inside space-y-0.5">
                      {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>Close</Button>
                <Button
                  onClick={async () => {
                    if (!importFile) { toast.error('Please select a file'); return; }
                    setImporting(true); setImportResult(null);
                    try {
                      const fd = new FormData();
                      fd.append('file', importFile);
                      const res = await axios.post(`${API}/suppliers/import${vtq()}`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      setImportResult(res.data);
                      toast.success(res.data.message || 'Import complete');
                      setRefreshKey((k) => k + 1);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Import failed');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!importFile || importing}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="confirm-import-suppliers-btn"
                >
                  {importing ? 'Importing…' : 'Import'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
